import React from "react";
import {
	Button,
	ClassNameProps,
	Conditional,
	Else,
	englishCommaJoinStrings,
	If,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	Announcement,
	CalendarEvent,
	Newsletter,
	Post,
	SharingPolicy,
	UserGroup,
	UserGroupNameAndColorFragment,
	useSharingPolicyPreviewQuery,
} from "../../../../Utilities";
import { SharingPolicyFormat, UserGroupFormat } from "../../../Format";
import { ControlledUsersListModal } from "../../User";
import styles from "./styles.module.css";

export type SharingViewProps = {
	announcementID?: Announcement["id"];
	calendarEventID?: CalendarEvent["id"];
	newsletterID?: Newsletter["id"];
	postID?: Post["id"];
	size?: "small" | "medium";
} & Partial<ClassNameProps & StyleProps>;

export function SharingView({
	announcementID,
	calendarEventID,
	newsletterID,
	postID,
	size = "medium",
	className,
	style,
}: SharingViewProps) {
	const { loading, data } = useSharingPolicyPreviewQuery({
		variables: {
			filters: {
				announcementIDEQ: announcementID,
				calendarEventIDEQ: calendarEventID,
				newsletterIDEQ: newsletterID,
				postIDEQ: postID,
			},
		},
	});
	const sharingPolicy = data?.sharingPolicyConnection.edges[0]?.node;
	const totalSharedUsers = data?.userConnection.totalCount ?? 0;
	const text = SharingPolicyFormat.preview(sharingPolicy, totalSharedUsers);
	const showViewButton = text !== "Private" && text !== "Everyone";

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const userFilters = React.useMemo(
		() => ({
			sharedSharingPolicies: [
				{
					announcementIDEQ: announcementID,
					calendarEventIDEQ: calendarEventID,
					newsletterIDEQ: newsletterID,
					postIDEQ: postID,
				},
			],
		}),
		[announcementID, calendarEventID, newsletterID, postID],
	);

	return (
		<React.Fragment>
			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.5rem"
				className={makeClassName(styles["sharing-view"], styles[`sharing-view--${size}`], className)}
				style={style}
			>
				<Icon.Users size={16} />

				<Conditional>
					<If condition={loading && !sharingPolicy}>Loading...</If>
					<Else>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							{text}
							<If condition={showViewButton}>
								<Button variant="link" size="small" onClick={openModal} className={styles["sharing-view__view-button"]}>
									View All
								</Button>
							</If>
						</Row>
					</Else>
				</Conditional>
			</Row>

			<Modal.If condition={isModalOpen}>
				<ControlledUsersListModal heading="Shared With" filters={userFilters} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

export type SimpleSharingViewProps = {
	sharingPolicy: Pick<SharingPolicy, "id" | "isPublic"> & {
		userGroups: (Pick<UserGroup, "id"> & UserGroupNameAndColorFragment)[];
	};
	size?: "small" | "medium";
} & Partial<ClassNameProps & StyleProps>;

/**
 * Simplified view component that does not make a network request to get the sharing record.
 *
 * @note Only displays content if public or shared with at least one user group.
 */
export function SimpleSharingView({ sharingPolicy, size, className, style }: SimpleSharingViewProps) {
	if (!sharingPolicy.isPublic && sharingPolicy.userGroups.length === 0) {
		return null;
	}

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.5rem"
			className={makeClassName(styles["simple-sharing-view"], styles[`simple-sharing-view--${size}`], className)}
			style={style}
		>
			<Icon.Users size={16} />
			<TruncatedText>
				{sharingPolicy.isPublic
					? "Everyone"
					: englishCommaJoinStrings(sharingPolicy.userGroups.map((userGroup) => UserGroupFormat.name(userGroup)))}
			</TruncatedText>
		</Row>
	);
}
